import { textSm, textSmSemibold } from '@insify/design-system/typography';
import { formatMoney } from '@insify/global-money';
import { formatMoneyPlaceholder } from '@insify/ui-utils';
import clsx from 'clsx';
import { forwardRef } from 'react';

import * as styles from './ReceiptItem.module.css';

import type { CountryCode } from '@insify/global-countries';
import type { MoneyField } from '@insify/global-money';

type Props = {
  country: CountryCode | Lowercase<CountryCode>;
  className?: string;
  titleClassName?: string;
  title?: React.ReactNode;
  amountClassName?: string;
  amount?: MoneyField;
  amountBeforeDiscount?: MoneyField;
  after?: string;
  isLoading?: boolean;
  afterClassName?: string;
  withoutFraction?: boolean;
  description?: React.ReactNode;
  amountOrPlaceholder?: React.ReactNode;
  'data-testid'?: string;
};

export const ReceiptItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      country,
      amount,
      amountBeforeDiscount,
      title,
      className,
      amountClassName,
      titleClassName,
      after,
      isLoading = false,
      afterClassName,
      withoutFraction = false,
      description,
      amountOrPlaceholder,
      'data-testid': dataTestId,
    },
    ref,
  ): JSX.Element => {
    amountOrPlaceholder = amountOrPlaceholder ?? (
      <span className='notranslate'>
        {amount ? formatMoney(amount, country, { withoutFraction }) : formatMoneyPlaceholder('EUR', country)}
      </span>
    );

    return (
      <div className={clsx(styles.receiptItem, className)} ref={ref}>
        <div className={styles.receiptRow}>
          <div className={clsx(styles.title, titleClassName)}>{title}</div>
          {amountBeforeDiscount && amountBeforeDiscount.cents !== amount?.cents && (
            <div className={clsx(textSmSemibold, styles.amountBeforeDiscount, 'notranslate')}>
              {formatMoney(amountBeforeDiscount, country)}
            </div>
          )}
          <span className={clsx(styles.amount, amountClassName, isLoading && styles.loading)} data-testid={dataTestId}>
            {amountOrPlaceholder}
          </span>
          {Boolean(after) && <span className={clsx(styles.after, afterClassName)}>{after}</span>}
        </div>
        <div className={clsx(textSm, styles.description)}>{description}</div>
      </div>
    );
  },
);
